import { useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { captureException } from '@sentry/react';
import { Button, Space } from 'antd';
import { capitalCase } from 'change-case';
import { LucideCircleCheckBig } from 'lucide-react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { InboxItem } from '@/services/database';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import { toast } from '@/shared/components/toasts/PlatformToast';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { RelativeTimeTooltip } from '@/shared/components/typography';
import { useInboxNotificationStore } from '@/stores';

import NotificationActionsDropdown from '../NotificationActionsDropdown';
import ResourceActivityIconAvatar from '../ResourceActivityIconAvatar';
import { ResourceActivityActorIdentity, ResourceActivityResourceIdentity } from '../ResourceActivityIdentity';

import './index.css';

interface NotificationMenuItemProps {
    item: InboxItem;
    onMarkRead?: (item: InboxItem) => void | Promise<void>;
    showResourceId?: boolean;
    showStatusControls?: boolean;
}

const NotificationMenuItem: FC<NotificationMenuItemProps> = ({
    item,
    onMarkRead,
    showResourceId = false,
    showStatusControls = true,
}) => {
    const { token } = useAntDesignTheme();
    const { activity } = item;
    const [markingRead, setMarkingRead] = useState(false);
    const markingReadRef = useRef(false);
    const deleting = useInboxNotificationStore((state) => state.deletingItemIds.includes(item.id));
    const unread = item.status === 'unread';
    const metadata: ReactNode[] = [];

    if (showResourceId) {
        metadata.push(<span key='resource-id'>{item.resourceId.slice(0, 8)}</span>);
    }

    metadata.push(
        <span key='resource-type' style={{ alignItems: 'center', display: 'inline-flex' }}>
            <ResourceActivityIconAvatar activity={activity} size={20} />
            <span style={{ marginLeft: '0.375rem' }}>{capitalCase(item.resourceType.replace(/s$/, ''))}</span>
        </span>,
        <RelativeTimeTooltip key='created-at' date={activity.createdAt} />,
    );

    return (
        <DropdownOptionContent
            control={
                showStatusControls ? (
                    <span
                        aria-label={unread ? 'Unread notification' : 'Read notification'}
                        role='img'
                        style={{
                            background: unread ? token.colorPrimary : token.colorTextQuaternary,
                            borderRadius: '50%',
                            display: 'block',
                            height: '0.375rem',
                            width: '0.375rem',
                        }}
                    />
                ) : undefined
            }
            detailStyle={{ marginTop: '0.25rem' }}
            extra={
                <Space.Compact size='small' style={{ alignItems: 'center' }}>
                    {showStatusControls && unread && onMarkRead ? (
                        <SmallTooltip
                            title='Mark as read'
                            placement='right'
                            padding='0'
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                            <Button
                                aria-label='Mark as read'
                                size='small'
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    height: '1.25rem',
                                    justifyContent: 'center',
                                    width: '1.25rem',
                                }}
                                type='text'
                                disabled={markingRead || deleting}
                                loading={markingRead}
                                onClick={async (event) => {
                                    event.stopPropagation();
                                    if (markingReadRef.current || deleting) {
                                        return;
                                    }
                                    markingReadRef.current = true;
                                    setMarkingRead(true);
                                    try {
                                        await onMarkRead(item);
                                    } catch (error) {
                                        captureException(error);
                                        toast.error('Could not mark notification read');
                                    } finally {
                                        markingReadRef.current = false;
                                        setMarkingRead(false);
                                    }
                                }}
                            >
                                <LucideCircleCheckBig
                                    size='1rem'
                                    style={{ color: token.colorTextTertiary, display: 'block' }}
                                />
                            </Button>
                        </SmallTooltip>
                    ) : null}
                    <NotificationActionsDropdown disabled={markingRead} item={item} />
                </Space.Compact>
            }
            metadata={metadata}
            title={
                <div className='notification-menu-item-headline'>
                    <span className='notification-menu-item-identity'>
                        <ResourceActivityActorIdentity activity={activity} />
                    </span>
                    <span
                        className='notification-menu-item-action'
                        title={activity.action}
                        style={{
                            color: token.colorTextSecondary,
                            flex: '0 1 auto',
                            minWidth: 0,
                            maxWidth: '45%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {activity.action}
                    </span>
                    <span className='notification-menu-item-identity notification-menu-item-resource'>
                        <ResourceActivityResourceIdentity item={item} />
                    </span>
                </div>
            }
        />
    );
};

export default NotificationMenuItem;
export type { NotificationMenuItemProps };
